import React, { useState, useEffect } from 'react';
import { CiSearch } from 'react-icons/ci';
import api from '../services/Api';
import { useNavigate } from 'react-router-dom';
import { FiEdit, FiTrash2 } from 'react-icons/fi';
import { HiOutlineOfficeBuilding } from 'react-icons/hi';
import IndustryModal from '../components/IndustryManagement/IndustryModal';
import { BeatLoader } from 'react-spinners';
import toast from 'react-hot-toast';
import {
  showConfirmToast,
  showError,
  showLoading,
  showSuccess,
} from '../components/Ui/ToastNotification';
import Title from '../components/Title';

const TOAST_IDS = {
  FETCH_LOADING: 'fetch-industries-loading',
  FETCH_SUCCESS: 'fetch-industries-success',
  FETCH_ERROR: 'fetch-industries-error',
  DELETE_SUCCESS: 'delete-industry-success',
  DELETE_ERROR: 'delete-industry-error',
  CONFIRM_DELETE: 'confirm-delete-industry',
};

const IndustryListing = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [allCompanies, setAllCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalState, setModalState] = useState({
    isIndustryModalOpen: false,
    isServiceModalOpen: false,
    modalMode: '',
  });

  const [industryFormData, setIndustryFormData] = useState({
    name: '',
    description: '',
    image: null,
    previewImage: '',
    updateId: '',
  });

  const fetchCompanies = async () => {
    setLoading(true);
    const loadingToastId = { id: TOAST_IDS.FETCH_LOADING };

    try {
      const response = await api.get('/industryListing/industries');
      setAllCompanies(response.data.industries);

      toast.dismiss(loadingToastId);
      showSuccess('Industry listings loaded successfully!', {
        id: TOAST_IDS.FETCH_SUCCESS,
      });
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.dismiss(loadingToastId);
      showError(
        error.response?.data?.message || 'Failed to fetch industry listings',
        {
          id: TOAST_IDS.FETCH_ERROR,
        },
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const openIndustryModal = (mode, company = null) => {
    setModalState((prev) => ({
      ...prev,
      isIndustryModalOpen: true,
      modalMode: mode,
    }));

    if (mode === 'update' && company) {
      setIndustryFormData({
        _id: company._id,
        name: company.name || '',
        description: company.description || '',
        status: company.isActive ? 'active' : 'inactive',
        tags: company.tags || [],
        image: null,
        previewImage: company.imageUrl || '',
        updateId: company._id || '',
      });
    } else {
      setIndustryFormData({
        name: '',
        description: '',
        status: 'active',
        tags: [],
        image: null,
        previewImage: '',
        updateId: '',
      });
    }
  };
  const closeIndustryModal = () => {
    setModalState((prev) => ({
      ...prev,
      isIndustryModalOpen: false,
    }));
    setIndustryFormData({
      name: '',
      description: '',
      image: null,
      previewImage: '',
      updateId: '',
    });
  };

  const handleAddService = () => {
    navigate('/services/add');
  };

  const handleViewServices = (industry) => {
    navigate(`/services/${industry._id}`);
  };

  const deleteCompany = async (id) => {
    showConfirmToast({
      message:
        'This will delete the industry and all associated service types!',
      toastId: `${TOAST_IDS.CONFIRM_DELETE}-${id}`,
      onConfirm: async () => {
        const loadingToastId = showLoading('Deleting industry...', {
          id: TOAST_IDS.DELETE_SUCCESS,
        });
        try {
          await api.delete(`/industryListing/industries/${id}`);
          setAllCompanies((prev) =>
            prev.filter((company) => company._id !== id),
          );
          toast.dismiss(loadingToastId);
          showSuccess('Industry deleted successfully!', {
            id: TOAST_IDS.DELETE_SUCCESS,
          });
        } catch (error) {
          console.error('Error deleting company:', error);
          toast.dismiss(loadingToastId);
          showError(
            error.response?.data?.message || 'Failed to delete industry',
            {
              id: TOAST_IDS.DELETE_ERROR,
            },
          );
        }
      },
      onCancel: () => {
        toast.dismiss(`${TOAST_IDS.CONFIRM_DELETE}-${id}`);
      },
    });
  };

  const filteredCompanies = allCompanies.filter((company) => {
    const name = company.name?.toLowerCase();
    return name?.includes(searchQuery.toLowerCase());
  });

  if (loading) {
    return (
      <div className="p-4 sm:ml-64 w-[80%] min-h-screen bg-[#F4F5F6] flex justify-center items-center">
        <BeatLoader color="#000000" size={15} data-testid="beat-loader" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:ml-64 w-[80%] min-h-[calc(100vh-63px)] bg-[#F4F5F6] font-nohemi">
      <div className="flex items-center justify-between mb-6">
        <Title title="Industry Listing" subtitle="Industries" />
        <div className="flex items-center justify-end">
          <button
            onClick={() => openIndustryModal('add')}
            className="px-4 py-2 mx-2 text-base text-white bg-black rounded-md text-nowrap hover:bg-black"
          >
            Add Industry
          </button>
          <button
            onClick={handleAddService}
            className="px-4 py-2 text-base text-white bg-black rounded-md text-nowrap hover:bg-black"
          >
            Add Service Type
          </button>
        </div>
      </div>

      <div className="min-h-screen block bg-[#FFFFFF] rounded-xl p-4">
        <div className="flex justify-start">
          <form className="max-w-md mx-8 my-4">
            <div className="relative">
              <div className="absolute inset-y-0 flex items-center pointer-events-none start-0 ps-3">
                <CiSearch />
              </div>
              <input
                type="search"
                className="block w-[260px] h-[40px] p-4 ps-10 text-base text-[#000000] rounded-[4px] bg-[#F5F6F7] focus:outline-none focus:ring-0 placeholder:text-[11px]"
                placeholder="Search Industry"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </form>
        </div>

        {filteredCompanies.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="p-6 mb-4 rounded-full bg-gray-50">
              <HiOutlineOfficeBuilding size={60} className="text-gray-300" />
            </div>
            <h3 className="text-xl font-semibold text-gray-800">
              No industry found
            </h3>
            <p className="max-w-xs mt-2 text-gray-500">
              It looks like there are no industries registered yet. Add a new
              industry to get started.
            </p>
            <button
              onClick={() => openIndustryModal('add')}
              className="px-6 py-2 mt-6 text-white transition bg-black rounded-md hover:bg-gray-800"
            >
              Add Industry
            </button>
          </div>
        ) : null}

        <div className="grid grid-cols-3 gap-6 p-4">
          {filteredCompanies.map((company) => (
            <div
              key={company._id}
              className="relative transition duration-300 bg-white border border-gray-200 rounded-md"
            >
              {/* Image Container */}
              <div className="w-full h-40 overflow-hidden bg-gray-100 rounded-t-md">
                <img
                  src={company.imageUrl}
                  alt={company.name}
                  className="object-cover w-full h-full"
                />
              </div>
              {/* Content */}
              <div className="px-4 py-2 space-y-1 text-base">
                {/* Company Name */}
                <p className="text-xl font-semibold truncate">{company.name}</p>
                <p className="w-32 px-2 py-1 bg-[#11CF24] text-white text-sm rounded">
                  {company.serviceType?.length || 0}{' '}
                  {company.serviceType?.length === 1 ? 'Service' : 'Services'}{' '}
                  Listed
                </p>
                {/* Description  */}
                <p className="text-sm text-gray-500 line-clamp-2">
                  {company.description}
                </p>
              </div>
              {/* Buttons Section */}
              <div className="flex items-center justify-between px-2 py-1 border-t">
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      openIndustryModal('update', company);
                    }}
                    className="p-2 text-gray-500 border border-gray-300 rounded-full hover:text-gray-700 hover:border-gray-700"
                  >
                    <FiEdit size={12} />
                  </button>
                  <button
                    onClick={() => deleteCompany(company._id)}
                    className="p-2 text-red-500 border border-red-300 rounded-full hover:text-red-700 hover:border-red-700"
                  >
                    <FiTrash2 size={12} />
                  </button>
                </div>

                <button
                  onClick={() => handleViewServices(company)}
                  className="px-3 py-1 text-sm text-black transition hover:underline hover:text-gray-700"
                >
                  View Services
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <IndustryModal
        isOpen={modalState.isIndustryModalOpen}
        onClose={closeIndustryModal}
        mode={modalState.modalMode}
        initialData={industryFormData}
        onSuccess={fetchCompanies}
      />
    </div>
  );
};

export default IndustryListing;
